import { useState, useEffect } from 'react';
import {
  getAllDiscussions,
  createDiscussion,
  addCommentToDiscussion,likeDislikeReaction
} from '../../../../services/discussionBox';
import validateByType from '../../../../hooks/useFormValidation';


const useDiscussBox = () => {
  const [form, setForm] = useState({ title: '', content: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [discussions, setDiscussions] = useState([]);
  const [commentInputs, setCommentInputs] = useState({});
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_count: 0,
    total_pages: 1,
    limit: 5,
  });

  useEffect(() => {
    fetchDiscussions(pagination.current_page, pagination.limit);
  }, [pagination.current_page]);

    const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
  };

  const fetchDiscussions = async (page = 1, limit = 5) => {
  try {
    const res = await getAllDiscussions( page , limit );
    const { data, pagination: meta } = res.data;
    setPagination((prev) => ({
        ...prev,
        current_page: meta.current_page,
        total_pages: meta.total_pages,
        total_count: meta.total_count,
      }));
    const discussionsData = res.data.data;
    const included = res.data.included;

    const commentsMap = {};
    const usersMap = {};

    // Step 1: Map comments and users
    included.forEach((item) => {
      if (item.type === 'comment') {
        commentsMap[item.id] = {
          ...item.attributes,
          id: item.id,
          userId: item.relationships.user.data.id,
        };
      } else if (item.type === 'user') {
        usersMap[item.id] = item.attributes;
      }
    });

    // Step 2: Map discussions with comments and reactions
    const discussionsWithComments = discussionsData.map((discussion) => {
      const commentIds = discussion.relationships.comments.data.map((c) => c.id);
      const comments = commentIds.map((id) => ({
        ...commentsMap[id],
        user: usersMap[commentsMap[id]?.userId] || null,
      }));
      // Extract like/dislike counts from reaction_counts
      const reactionCounts = discussion.attributes.reactions_counts || {};
      const likeCount = reactionCounts.like || 0;
      const dislikeCount = reactionCounts.dislike || 0;

      return {
        id: discussion.id,
        title: discussion.attributes.title,
        content: discussion.attributes.content,
        highlighted: discussion.attributes.highlighted,
        comments,
        currentUserReaction: discussion?.attributes?.current_user_reaction,
        reactions: {
          like: likeCount,
          dislike: dislikeCount
        }
      };
    });

    setDiscussions(discussionsWithComments);
  } catch (err) {
    console.error("Failed to load discussions", err);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update form
    setForm((prev) => ({ ...prev, [name]: value }));

    // Clear error for the current field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };


  const resetForm = () => setForm({ title: '', content: '' });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const validationErrors = validateByType('discussionBox', form);
    setErrors(validationErrors);

    // If there are any validation errors, stop submission
    if (Object.keys(validationErrors).length > 0) {
      setLoading(false);
      return;
    }

    try {
      const payload = {
        discussion: {
          title: form.title,
          content: form.content,
        },
      };
      await createDiscussion(payload);
      resetForm();
      await fetchDiscussions(pagination.page, pagination.limit); // Refresh discussions
    } catch (err) {
      console.error("Failed to submit discussion", err);
    }

    setLoading(false);
  };


  const addComment = async (discussionId) => {
    const commentText = commentInputs[discussionId]?.trim();
    if (!commentText) return;

    try {
      // Send new comment to backend
      const res = await addCommentToDiscussion(discussionId, commentText);

      // The backend should return the created comment with user info
      const newComment = {
        id: res.data.data.id,
        ...res.data.data.attributes,
        user: res.data.included?.find((inc) => inc.type === "user")?.attributes || null
      };

      // Update discussions locally
      setDiscussions((prevDiscussions) =>
        prevDiscussions.map((discussion) => {
          if (discussion.id === discussionId) {
            return {
              ...discussion,
              comments: [...discussion.comments, newComment]
            };
          }
          return discussion;
        })
      );

      // Clear the input box
      setCommentInputs((prev) => ({ ...prev, [discussionId]: '' }));

    } catch (err) {
      console.error("Failed to add comment", err);
    }
  };


  const updateSingleDiscussion = async (discussionId) => {
    try {
      const res = await getAllDiscussions();
      const discussionsData = res.data.data;
      const included = res.data.included;

      const commentsMap = {};
      const usersMap = {};

      included.forEach((item) => {
        if (item.type === 'comment') {
          commentsMap[item.id] = {
            ...item.attributes,
            id: item.id,
            userId: item.relationships.user.data.id,
          };
        } else if (item.type === 'user') {
          usersMap[item.id] = item.attributes;
        }
      });

      const updatedDiscussionRaw = discussionsData.find((d) => d.id === discussionId);
      const commentIds = updatedDiscussionRaw.relationships.comments.data.map((c) => c.id);
      const comments = commentIds.map((id) => ({
        ...commentsMap[id],
        user: usersMap[commentsMap[id]?.userId] || null,
      }));

      const updatedDiscussion = {
        id: updatedDiscussionRaw.id,
        title: updatedDiscussionRaw.attributes.title,
        content: updatedDiscussionRaw.attributes.content,
        comments,
      };

      setDiscussions((prev) =>
        prev.map((d) => (d.id === updatedDiscussion.id ? updatedDiscussion : d))
      );
    } catch (err) {
      console.error("Failed to refresh discussion", err);
    }
  };

   const handleReaction = async (discussionId, type) => {
  try {
    await likeDislikeReaction(discussionId, type); // Send to backend

    setDiscussions((prevDiscussions) =>
      prevDiscussions.map((discussion) => {
        if (discussion.id === discussionId) {
          let updatedReactions = { ...discussion.reactions };
          let currentReaction = discussion.currentUserReaction;

          if (currentReaction === type) {
            // User clicked the same reaction → remove it
            if (updatedReactions[type] > 0) {
              updatedReactions[type] -= 1;
            }
            return {
              ...discussion,
              reactions: updatedReactions,
              currentUserReaction: null
            };
          } else {
            // Remove old reaction if any
            if (currentReaction && updatedReactions[currentReaction] > 0) {
              updatedReactions[currentReaction] -= 1;
            }

            // Add new reaction
            if (type) {
              updatedReactions[type] = (updatedReactions[type] || 0) + 1;
            }

            return {
              ...discussion,
              reactions: updatedReactions,
              currentUserReaction: type
            };
          }
        }
        return discussion;
      })
    );

  } catch (err) {
    console.error(`Failed to ${type} the discussion`, err);
  }
};


  return {
    form,
    errors,
    loading,
    discussions,
    pagination,
    commentInputs,
    handleChange,
    onSubmit,
    addComment,
    setCommentInputs,handlePageChange,handleReaction
  };
};

export default useDiscussBox;
